import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { useSnackbar } from "components/haven-design/snackbar";
import { usePostgresViewer } from "components/viewer/context";
import { userLoginToken } from "utils/general-utils";
import { UserRole } from "types/gql-types";


const viewerHasToken = () => !!userLoginToken();

const userHasRole = (viewerRoles: UserRole[], allowedRoles: UserRole[]) => {
  if (viewerRoles.includes(UserRole.ADMIN)) return true; // admin always has permission
  // if a set of user roles has one or more of allowed roles, return true
  return viewerRoles.some((role) => allowedRoles.includes(role));
};

// Styles migrated to `sx` props on components

const NotAuthorized: React.FC = () => (
  <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 3 }}>
    <Container maxWidth="sm">
      <Paper elevation={1} sx={{ maxWidth: 560, width: "100%", textAlign: "center", padding: 3 }}>
        <Typography component="h2" variant="h5" sx={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
          Access denied
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", marginTop: 1, marginBottom: 2 }}>
          You don’t have permission to view this page. If you believe this is an error,
          please contact your administrator.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          href="/"
          sx={{ marginTop: 1 }}
        >
          Return to Home
        </Button>
      </Paper>
    </Container>
  </Box>
);

/**
 * HOC to wrap a page and only render if viewer has the correct role(s)
 * passes parent props through and adds viewer prop
 **/
export const authorizeForRole = <P extends Record<string, unknown>>(
  allowedRoles: UserRole[],
  Component: React.ComponentType<P & { viewer: NonNullable<ReturnType<typeof usePostgresViewer>> }>,
): React.ComponentType<P> => function Authorizer (props: P) {
  const viewer = usePostgresViewer();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    if (!viewerHasToken() || !viewer) {
      openSnackbar({ message: "Please log in to access this page", status: "alert", duration: 5000 });
      localStorage.setItem("requestedPath", location.pathname);
      navigate("/login", { replace: true });
    }
  }, [viewer]);

  if (!viewerHasToken() || !viewer) return null;

  const { roles } = viewer;

  if (userHasRole(roles as UserRole[], allowedRoles)) {
    return <Component {...props as P} viewer={viewer} />;
  }

  return <NotAuthorized />;
};

